
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { useProjectMeta } from '@/hooks/meta/useProjectMeta';
import { templateService } from '@/services/template';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

interface CreateTimelineWithChaptersDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    // storyId and projectId removed
    onCreated: (timelineId: number) => void;
}

export const CreateTimelineWithChaptersDialog: React.FC<CreateTimelineWithChaptersDialogProps> = ({
    open,
    onOpenChange,
    // storyId and projectId removed
    onCreated
}) => {
    const { projectId } = useParams(); // Get projectId from URL
    const [title, setTitle] = useState("");
    const [chapterCount, setChapterCount] = useState<number>(1);
    const [isCreating, setIsCreating] = useState(false);
    const [selectedStoryId, setSelectedStoryId] = useState<string>("");
    const { toast } = useToast();

    // Auto-detect current story
    const { currentStoryId } = useProjectMeta(projectId || null);

    useEffect(() => {
        if (open) {
            setTitle("");
            setChapterCount(1);
            setIsCreating(false);

            // Use auto-detected active story
            if (currentStoryId) {
                setSelectedStoryId(currentStoryId);
            } else {
                setSelectedStoryId("");
            }
        }
    }, [open, currentStoryId]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!title.trim()) {
            toast({
                title: "Error",
                description: "Please enter a timeline name.",
                variant: "destructive"
            });
            return;
        }
        if (!selectedStoryId) {
            toast({
                title: "Error",
                description: "Please select a story.",
                variant: "destructive"
            });
            return;
        }

        try {
            setIsCreating(true);
            const result = await templateService.createTimelineWithChapters(
                selectedStoryId,
                title,
                chapterCount
            );

            if (result.success && result.timeline) {
                toast({
                    title: "Timeline Created",
                    description: `Successfully created "${title}" with ${chapterCount} chapters.`
                });
                onCreated(result.timeline.id);
                onOpenChange(false);
            } else {
                throw new Error(result.error || "Failed to create timeline");
            }
        } catch (error) {
            console.error("Error creating timeline with chapters:", error);
            toast({
                title: "Error",
                description: "Failed to create timeline. Please try again.",
                variant: "destructive"
            });
        } finally {
            setIsCreating(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[600px] sm:rounded-2xl border-border/60 shadow-2xl p-0 overflow-hidden gap-0">
                <DialogHeader className="p-8 pb-6 border-b border-border/40 bg-muted/5 space-y-1">
                    <DialogTitle className="text-2xl font-semibold text-foreground tracking-tight">Start Blank Canvas</DialogTitle>
                    <DialogDescription className="text-base text-muted-foreground leading-relaxed">
                        Create a new timeline and define how many chapters you want to start with.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit}>
                    <div className="space-y-6 px-8 py-8">
                        <div className="space-y-2.5">
                            <Label htmlFor="timeline-title" className="text-sm font-medium text-foreground">Timeline Name</Label>
                            <Input
                                id="timeline-title"
                                placeholder="Main Storyline"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                disabled={isCreating}
                                autoFocus
                                className="h-11 rounded-xl bg-muted/30 border-muted-foreground/10 focus:border-primary/20 focus:ring-primary/20 transition-all font-medium text-foreground placeholder:text-muted-foreground/50 shadow-sm"
                            />
                        </div>

                        {/* Story is auto-detected */}

                        <div className="space-y-2.5">
                            <Label htmlFor="chapter-count" className="text-sm font-medium text-foreground">Number of Chapters</Label>
                            <Input
                                id="chapter-count"
                                type="number"
                                min={1}
                                max={50}
                                value={chapterCount}
                                onChange={(e) => setChapterCount(parseInt(e.target.value) || 1)}
                                disabled={isCreating}
                                className="h-11 rounded-xl bg-muted/30 border-muted-foreground/10 focus:border-primary/20 focus:ring-primary/20 transition-all font-medium text-foreground shadow-sm"
                            />
                            <p className="text-xs text-muted-foreground">
                                We'll generate empty chapters for you (e.g., "Chapter 1", "Chapter 2").
                            </p>
                        </div>
                    </div>

                    <DialogFooter className="p-6 bg-muted/5 border-t border-border/40">
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => onOpenChange(false)}
                            disabled={isCreating}
                            className="min-w-[100px] rounded-xl hover:bg-muted/50"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={isCreating || !title.trim() || !selectedStoryId}
                            className="min-w-[120px] rounded-xl font-medium shadow-sm active:scale-95 transition-all text-base"
                        >
                            {isCreating ? "Creating..." : "Create Timeline"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
